import React, { useState, useEffect } from "react";
import { scenarios } from "./data/scenarios";
import Scene from "./components/Scene";
import DialogBox from "./components/DialogBox";
import Choices from "./components/Choices";
import StatsBar from "./components/StatsBar";
import "./styles/App.css";

function App() {
  const [playerName, setPlayerName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [currentId, setCurrentId] = useState(0);
  const [text, setText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [stats, setStats] = useState({ courage: 1, sagesse: 1 });
  const [saveStatus, setSaveStatus] = useState("");
  const [step, setStep] = useState("name"); // 'name', 'stats', 'game'
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const scene = scenarios.find((s) => s.id === currentId);

  useEffect(() => {
    if (!playerName || !scene) return;
    console.log(
      "Scene:",
      scene.id,
      "text:",
      scene.text,
      "end:",
      scene.end,
      "choices:",
      scene.choices,
    );
    if (scene.aiPrompt) {
      generateDialog(scene.aiPrompt);
    } else {
      setText(scene.text);
      setShowNext(!scene.choices && !scene.end);
    }
  }, [currentId, playerName]);

  // Sauvegarde automatique à chaque changement de scène
  useEffect(() => {
    if (!playerName) return;
    saveProgress();
  }, [currentId, stats]);

  async function generateDialog(prompt) {
    setIsLoading(true);
    setText("");
    try {
      const res = await fetch(
        "https://medieval-vn-server-production.up.railway.app/dialog",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt, stats }),
        },
      );
      const data = await res.json();
      setText(data.text);
      setShowNext(true);
    } catch (e) {
      setText("— Je n'ai rien à dire pour l'instant...");
      setShowNext(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function saveProgress() {
    if (!playerName || playerName.trim() === "") return;
    const historyEntry = {
      sceneId: currentId,
      courage: stats.courage,
      sagesse: stats.sagesse,
      label:
        scenarios.find((s) => s.id === currentId)?.text?.slice(0, 40) ||
        `Scène ${currentId}`,
    };

    // Mise à jour du state local
    setHistory((prev) => {
      if (prev.length && prev[prev.length - 1].sceneId === currentId)
        return prev;
      return [...prev, historyEntry];
    });

    try {
      await fetch("https://medieval-vn-server-production.up.railway.app/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerName,
          sceneId: currentId,
          courage: stats.courage,
          sagesse: stats.sagesse,
          historyEntry,
        }),
      });
      setSaveStatus("Progression sauvegardée");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (e) {
      console.error("Erreur sauvegarde", e);
    }
  }

  async function handleStart() {
    if (!nameInput.trim()) return;
    setPlayerName(nameInput.trim());

    try {
      const res = await fetch(
        `https://medieval-vn-server-production.up.railway.app/save/${nameInput.trim()}`,
      );
      const data = await res.json();
      if (data.save) {
        setCurrentId(data.save.scene_id);
        setStats({ courage: data.save.courage, sagesse: data.save.sagesse });
        setHistory(JSON.parse(data.save.history || "[]"));
        setSaveStatus("Partie précédente chargée !");
        setTimeout(() => setSaveStatus(""), 3000);
        setStep("game");
      } else {
        setStats({ courage: 1, sagesse: 1 }); // reset pour la répartition
        setStep("stats"); // pas de sauvegarde → écran stats
      }
    } catch (e) {
      setStats({ courage: 1, sagesse: 1 });
      setStep("stats");
    }
  }

  function handleChoice(choice) {
    setStats((prev) => ({
      courage: Math.max(
        0,
        Math.min(10, prev.courage + (choice.stats?.courage || 0)),
      ),
      sagesse: Math.max(
        0,
        Math.min(10, prev.sagesse + (choice.stats?.sagesse || 0)),
      ),
    }));
    goTo(choice.next);
  }

  function goTo(id) {
    setShowNext(false);
    setText("");
    setCurrentId(id);
  }

  async function restart() {
    await fetch(
      `https://medieval-vn-server-production.up.railway.app/save/${playerName}`,
      {
        method: "DELETE",
      },
    );
    setStep("name");
    setPlayerName("");
    setNameInput("");
    setStats({ courage: 1, sagesse: 1 });
    setCurrentId(0);
    setText("");
    setShowNext(false);
  }

  // Écran de démarrage
  // Écran nom
  if (step === "name") {
    return (
      <div className="vn-start">
        <h1 className="vn-title">⚔️ Les Ruines d'Andor</h1>
        <p className="vn-subtitle">Un visual novel fantasy assisté par IA</p>
        <div className="vn-start-form">
          <input
            type="text"
            placeholder="Entre ton nom d'aventurier..."
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStart()}
            className="vn-name-input"
          />
          <button className="vn-start-btn" onClick={handleStart}>
            Continuer →
          </button>
        </div>
      </div>
    );
  }

  // Écran stats
  if (step === "stats") {
    return (
      <div className="vn-start">
        <h1 className="vn-title">Choisissez votre destin</h1>
        <p className="vn-subtitle">
          Répartissez vos points avant de partir à l'aventure
        </p>

        <div className="vn-points-left">
          <span>{10 - stats.courage - stats.sagesse}</span>
          <p>points restants</p>
        </div>

        <div className="vn-stat-cards">
          <div className="vn-stat-card">
            <div className="vn-stat-header">
              <span className="vn-stat-name">⚔️ Courage</span>
              <span className="vn-stat-val">{stats.courage}</span>
            </div>
            <p className="vn-stat-desc">
              Influence le ton des personnages face à ta bravoure. Un courage
              élevé impressionne Lyra et rassure le Roi.
            </p>
            <div className="vn-stat-controls">
              <button
                className="vn-stat-btn"
                onClick={() =>
                  setStats((p) => ({
                    ...p,
                    courage: Math.max(1, p.courage - 1),
                  }))
                }
                disabled={stats.courage <= 1}
              >
                −
              </button>
              <div className="vn-stat-bar">
                <div
                  className="vn-stat-fill"
                  style={{ width: `${(stats.courage / 8) * 100}%` }}
                />
              </div>
              <button
                className="vn-stat-btn"
                onClick={() =>
                  setStats((p) => ({
                    ...p,
                    courage: Math.min(8, p.courage + 1),
                  }))
                }
                disabled={
                  10 - stats.courage - stats.sagesse <= 0 || stats.courage >= 8
                }
              >
                +
              </button>
            </div>
          </div>

          <div className="vn-stat-card">
            <div className="vn-stat-header">
              <span className="vn-stat-name">📖 Sagesse</span>
              <span className="vn-stat-val">{stats.sagesse}</span>
            </div>
            <p className="vn-stat-desc">
              Détermine ta capacité à analyser et comprendre. Une sagesse élevée
              intrigue Lyra et te donne accès à plus d'informations.
            </p>
            <div className="vn-stat-controls">
              <button
                className="vn-stat-btn"
                onClick={() =>
                  setStats((p) => ({
                    ...p,
                    sagesse: Math.max(1, p.sagesse - 1),
                  }))
                }
                disabled={stats.sagesse <= 1}
              >
                −
              </button>
              <div className="vn-stat-bar">
                <div
                  className="vn-stat-fill"
                  style={{ width: `${(stats.sagesse / 8) * 100}%` }}
                />
              </div>
              <button
                className="vn-stat-btn"
                onClick={() =>
                  setStats((p) => ({
                    ...p,
                    sagesse: Math.min(8, p.sagesse + 1),
                  }))
                }
                disabled={
                  10 - stats.courage - stats.sagesse <= 0 || stats.sagesse >= 8
                }
              >
                +
              </button>
            </div>
          </div>
        </div>

        <button
          className="vn-start-btn"
          onClick={() => setStep("game")}
          disabled={10 - stats.courage - stats.sagesse !== 0}
        >
          Commencer l'aventure →
        </button>
      </div>
    );
  }

  if (!playerName || step !== "game") return null;
  if (!scene) return null;

  console.log(
    "render - scene:",
    currentId,
    "text:",
    text,
    "showNext:",
    showNext,
    "isLoading:",
    isLoading,
    "end:",
    scene?.end,
  );

  return (
    <div className="vn-wrapper">
      <StatsBar stats={stats} scene={currentId + 1} />
      <button
        className="vn-restart-btn"
        onClick={() => {
          setStep("name");
          setPlayerName("");
          setNameInput("");
          setStats({ courage: 1, sagesse: 1 });
          setCurrentId(0);
          setText("");
          setShowNext(false);
        }}
      >
        ↺ Recommencer
      </button>

      {/* Bouton historique */}
      <button
        className="vn-restart-btn"
        onClick={() => setShowHistory(!showHistory)}
      >
        📜 Historique
      </button>

      {/* Panneau historique */}
      {showHistory && (
        <div className="vn-history-panel">
          <h4>Reprendre depuis...</h4>
          {history.length === 0 && (
            <p className="vn-history-empty">Aucune sauvegarde</p>
          )}
          {[...history].reverse().map((entry, i) => (
            <button
              key={i}
              className="vn-history-entry"
              onClick={() => {
                setCurrentId(entry.sceneId);
                setStats({ courage: entry.courage, sagesse: entry.sagesse });
                setShowHistory(false);
                setText("");
                setShowNext(false);
              }}
            >
              <span className="vn-history-scene">
                Scène {entry.sceneId + 1}
              </span>
              <span className="vn-history-label">{entry.label}...</span>
              <span className="vn-history-stats">
                ⚔️ {entry.courage} 📖 {entry.sagesse}
              </span>
            </button>
          ))}
        </div>
      )}

      {saveStatus && <div className="vn-save-status">{saveStatus}</div>}
      <Scene background={scene.background} character={scene.character} />
      <div className="vn-dialog">
        <DialogBox
          speaker={scene.character}
          text={text}
          isLoading={isLoading}
        />
        {!isLoading &&
          (scene.end ? (
            <div className="vn-choices">
              <button className="vn-choice" onClick={restart}>
                ↺ Recommencer l'aventure
              </button>
            </div>
          ) : (
            <Choices
              choices={scene.choices}
              onChoice={handleChoice}
              onNext={() => goTo(scene.next)}
              showNext={showNext}
            />
          ))}
      </div>
    </div>
  );
}

export default App;
